import React, { useEffect, useState } from 'react';
import { ArrowLeft, Share2, Star, Car, Route, History, MapPin, Phone, XCircle, ShieldCheck } from 'lucide-react';
import { makePhoneCall, triggerHapticFeedback } from '../../utils/capacitorHelper';

const YACHANGU_LOGO =
  'https://customer-assets.emergentagent.com/job_yachangu-pay/artifacts/ynseryay_EE6C3FEA-5C5E-424A-B08C-ED1F2A53B790.jpeg';

const glassPanel = {
  backgroundColor: 'rgba(255, 255, 255, 0.72)',
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
};

// Resolve a possibly-relative upload path to an absolute backend URL
const resolvePhoto = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

/**
 * DriverProfileSheet — full-screen, glassmorphic driver profile shown during an
 * active ride. Adapted from the Stitch "Driver Profile" design with Yachangu
 * branding, emerald palette and real driver stats from /driver-profile.
 */
const DriverProfileSheet = ({ rideDetails, driverDetails, eta, onClose, onCancelRide }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/rides/${rideDetails?.id}/driver-profile`
        );
        const data = await res.json();
        if (active && data?.success) setProfile(data);
      } catch (e) {
        console.error('Driver profile load error', e);
      } finally {
        if (active) setLoading(false);
      }
    };
    if (rideDetails?.id) load();
    return () => {
      active = false;
    };
  }, [rideDetails?.id]);

  const driver = profile?.driver || driverDetails || {};
  const vehicle = profile?.vehicle || driverDetails?.vehicle || {};
  const stats = profile?.stats || {};
  const photo =
    resolvePhoto(driver.face_photo_url || driver.profile_picture) ||
    'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=600&q=80';

  const pickupName = rideDetails?.pickup_location?.address || 'Pickup point';
  const dropoffName = rideDetails?.dropoff_location?.address || 'Destination';
  const fare = rideDetails?.estimated_price;
  const tier = stats.tier || 'Standard';

  const arrival = (() => {
    if (!eta) return null;
    const mins = parseInt(eta, 10);
    if (Number.isNaN(mins)) return null;
    const d = new Date(Date.now() + mins * 60000);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  })();

  return (
    <div
      className="fixed inset-0 z-[80] overflow-y-auto bg-[#f7f9fb] pb-32"
      data-testid="driver-profile-sheet"
    >
      {/* Glass header with Yachangu branding */}
      <header
        className="fixed top-4 left-4 right-4 z-50 rounded-2xl border border-white/40 shadow-[0_20px_40px_rgba(0,0,0,0.08)] flex justify-between items-center px-5 h-16"
        style={glassPanel}
      >
        <button
          onClick={onClose}
          data-testid="driver-profile-back"
          className="active:scale-95 transition-transform text-emerald-700"
          aria-label="Back"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <img src={YACHANGU_LOGO} alt="Yachangu" className="w-7 h-7 rounded-lg object-cover" />
          <span className="font-extrabold tracking-tight text-emerald-700 text-lg">Yachangu</span>
        </div>
        <button className="text-emerald-700 active:scale-95 transition-transform" aria-label="Share">
          <Share2 className="w-5 h-5" />
        </button>
      </header>

      {/* Hero driver photo */}
      <section className="relative h-[420px] w-full overflow-hidden">
        <img src={photo} alt={driver.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70 flex flex-col justify-end p-7">
          <div className="flex items-end justify-between">
            <div>
              <h1 className="text-3xl font-extrabold text-white mb-1" data-testid="driver-profile-name">
                {driver.name || 'Driver'}
              </h1>
              <p className="text-white/85 flex items-center gap-2 text-sm font-medium">
                <ShieldCheck className="w-4 h-4 text-emerald-300" />
                {tier} Yachangu Driver
              </p>
            </div>
            <div className="bg-emerald-500/90 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg flex items-center gap-1">
              <Star className="w-4 h-4 fill-white" />
              {(stats.avg_rating ?? 5.0).toFixed(2)}
            </div>
          </div>
        </div>
      </section>

      {/* Floating content */}
      <div className="px-6 -mt-10 relative z-10 space-y-6">
        {/* Bento stats */}
        <div
          className="rounded-3xl border border-white/50 p-6 shadow-[0_20px_40px_rgba(16,185,129,0.08)] grid grid-cols-3 gap-3"
          style={glassPanel}
          data-testid="driver-profile-stats"
        >
          <div className="flex flex-col items-center text-center">
            <Car className="w-6 h-6 text-emerald-600 mb-2" />
            <span className="text-sm font-semibold text-gray-800 leading-tight">
              {vehicle?.make || ''} {vehicle?.model || 'Vehicle'}
            </span>
            <span className="text-[11px] text-gray-500 font-medium tracking-wider mt-0.5">
              {vehicle?.plate_number || '—'}
            </span>
          </div>
          <div className="flex flex-col items-center text-center border-x border-gray-200/70 px-1">
            <Route className="w-6 h-6 text-emerald-600 mb-2" />
            <span className="text-sm font-semibold text-gray-800">
              {(stats.total_trips ?? 0).toLocaleString()}
            </span>
            <span className="text-[11px] text-gray-500 font-medium tracking-wider mt-0.5">TOTAL TRIPS</span>
          </div>
          <div className="flex flex-col items-center text-center">
            <History className="w-6 h-6 text-emerald-600 mb-2" />
            <span className="text-sm font-semibold text-gray-800">
              {stats.experience_years > 0
                ? `${stats.experience_years} yr${stats.experience_years > 1 ? 's' : ''}`
                : stats.member_since_year || '—'}
            </span>
            <span className="text-[11px] text-gray-500 font-medium tracking-wider mt-0.5">
              {stats.experience_years > 0 ? 'EXPERIENCE' : 'MEMBER SINCE'}
            </span>
          </div>
        </div>

        {/* Active journey */}
        <div className="rounded-3xl border border-white/50 p-6 shadow-sm" style={glassPanel}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Active Journey</h2>
            <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-lg text-xs font-semibold">
              In Progress
            </span>
          </div>
          <div className="space-y-6 relative">
            <div className="absolute left-[11px] top-6 bottom-6 w-0.5 bg-gradient-to-b from-emerald-500 to-gray-300" />
            <div className="flex gap-4 relative">
              <div className="w-6 h-6 rounded-full bg-emerald-500 border-4 border-white shadow-sm z-10" />
              <div>
                <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-widest">Pickup</p>
                <p className="text-sm font-medium text-gray-800">{pickupName}</p>
              </div>
            </div>
            <div className="flex gap-4 relative">
              <div className="w-6 h-6 rounded-full bg-gray-900 border-4 border-white shadow-sm z-10 flex items-center justify-center">
                <MapPin className="w-3 h-3 text-white" />
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-widest">Destination</p>
                <p className="text-sm font-medium text-gray-800">{dropoffName}</p>
              </div>
            </div>
          </div>
          <div className="mt-7 pt-5 border-t border-gray-200/70 flex justify-between items-center">
            <div>
              <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-widest">Est. Fare</p>
              <p className="text-xl font-bold text-emerald-600">
                {fare != null ? `MWK ${fare.toLocaleString()}` : '—'}
              </p>
            </div>
            {arrival && (
              <div className="text-right">
                <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-widest">Arrival</p>
                <p className="text-xl font-bold text-gray-900">{arrival}</p>
              </div>
            )}
          </div>
        </div>

        {/* Bio + languages */}
        {(driver.bio || (driver.languages && driver.languages.length > 0)) && (
          <div className="py-1">
            {driver.bio && (
              <>
                <h3 className="text-lg font-bold text-gray-900 mb-2">About {driver.name?.split(' ')[0]}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{driver.bio}</p>
              </>
            )}
            {driver.languages && driver.languages.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {driver.languages.map((lang) => (
                  <span
                    key={lang}
                    className="bg-emerald-50 px-4 py-2 rounded-full text-xs font-medium text-emerald-700"
                  >
                    {lang}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => {
              triggerHapticFeedback('light');
              makePhoneCall(driver.phone);
            }}
            data-testid="driver-profile-call"
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-14 rounded-full font-semibold flex items-center justify-center gap-2 shadow-[0_10px_20px_rgba(16,185,129,0.25)] active:scale-95 transition-all"
          >
            <Phone className="w-5 h-5" />
            Contact Driver
          </button>
          {onCancelRide && (
            <button
              onClick={onCancelRide}
              data-testid="driver-profile-cancel"
              className="bg-white border border-red-400 text-red-600 h-14 rounded-full font-semibold flex items-center justify-center gap-2 active:scale-95 transition-all"
            >
              <XCircle className="w-5 h-5" />
              Cancel Trip
            </button>
          )}
        </div>

        {loading && (
          <p className="text-center text-xs text-gray-400 pb-4" data-testid="driver-profile-loading">
            Loading latest driver stats…
          </p>
        )}
      </div>
    </div>
  );
};

export default DriverProfileSheet;
